import { API_ROUTES } from '@/common/constants'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import {
    campaignPledge,
    campaignSubscribe,
    campaignUnsubscribe,
    getAgencyAgreement,
    getCampaignDetails,
    getCampaignSubscriptions,
    getCampaignsList,
    getPledgeBySecurityCode,
    submitAgencyAgreement,
} from './campaignsApi'
import { Maybe } from '@/common/types'
import { AgencyAgreementT } from './types'

export const useFetchCampaignsList = () => {
    return useQuery({
        queryKey: [API_ROUTES.campaignsList],
        queryFn: getCampaignsList,
    })
}

export const useFetchCampaignDetails = (webflowItemId: Maybe<string>) => {
    return useQuery({
        queryKey: [API_ROUTES.campaignDetails, webflowItemId],
        queryFn: () => getCampaignDetails(webflowItemId ?? ''),
        enabled: !!webflowItemId,
    })
}

export const useFetchSubscriptions = () => {
    return useQuery({
        queryKey: [API_ROUTES.campaignSubscriptions],
        queryFn: getCampaignSubscriptions,
    })
}

export const useGetAgencyAgreement = () => {
    return useQuery({
        queryKey: [API_ROUTES.agencyAgreement],
        queryFn: getAgencyAgreement,
    })
}

export const useSubmitAgencyAgreement = () => {
    return useMutation({
        mutationFn: (agencyAgreement: AgencyAgreementT) => submitAgencyAgreement(agencyAgreement),
    })
}

export const useSubscribeCampaign = () => {
    return useMutation({
        mutationFn: (campaignId: string) => campaignSubscribe(campaignId),
    })
}

export const useUnsubscribeCampaign = () => {
    return useMutation({
        mutationFn: (campaignId: string) => campaignUnsubscribe(campaignId),
    })
}

type PledgeRequest = {
    campaignId: string
    securityCode: string
}

export const usePledgeForCampaign = () => {
    return useMutation({
        mutationFn: (pledge: PledgeRequest) => campaignPledge(pledge.campaignId, pledge.securityCode),
    })
}

export const useCampaignPledgeFetcher = (campaignId: string, securityCodes: string[]) => {
    return useQueries({
        queries: securityCodes.map((code) => ({
            queryKey: [API_ROUTES.campaignPledge, campaignId, code],
            queryFn: () => getPledgeBySecurityCode(campaignId, code),
            refetchOnWindowFocus: false,
            staleTime: 1000 * 60 * 5,
            retry: (count: number, err: any) => err?.response?.status !== 429 && count < 3,
            retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 30000),
        })),
    })
}

export const useFetchCampaignPledgeFetcher = () => {
    const queryClient = useQueryClient()
    return useCallback(
        (campaignId: string, securityCode: string) => {
            return queryClient.fetchQuery({
                queryKey: [API_ROUTES.campaignPledge, campaignId, securityCode],
                queryFn: () => getPledgeBySecurityCode(campaignId, securityCode),
                staleTime: 1000 * 60 * 5,
                retry: (count: number, err: any) => err?.response?.status !== 429 && count < 3,
                retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 30000),
            })
        },
        [queryClient],
    )
}
